import React, { useState, useEffect, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { getServers, createServer, updateServer, deleteServer } from '../../actions/servers'
import Loader from '../../components/Loader'
import Table from '../../components/Table'
import ServerModal from '../../components/ServerModal'
import TableSearch from '../../components/TableSearch'
import { IoIosCheckmark, IoIosClose, IoMdCreate, IoMdTrash, IoMdAdd } from 'react-icons/io'
import moment from 'moment'
import styles from './styles.module.scss'

const Servers = () => {
  const dispatch = useDispatch()
  const { servers, loading } = useSelector(state => state.servers)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingServer, setEditingServer] = useState(null)
  const [filteredData, setFilteredData] = useState(null)
  const [sortState, setSortState] = useState({ key: null, order: null })
  const [openSearchKey, setOpenSearchKey] = useState(null)
  const [showMo, setShowMo] = useState(false)

  useEffect(() => {
    dispatch(getServers())
  }, [])

  const handleFilter = useCallback((result, query) => {
    if (!query) {
      setFilteredData(null)
      return
    }
    setFilteredData(result || [])
  }, [])

  const handleSearchOpen = useCallback((searchKey) => {
    setOpenSearchKey(searchKey)
  }, [])

  const handleSort = useCallback((sortedData, sortKey, sortOrder) => {
    setSortState({ key: sortOrder ? sortKey : null, order: sortOrder })
  }, [])

  // Вспомогательная функция для проверки значения поля
  const isFieldTrue = (value) => {
    return value === true || value === 1 || value === '1' || value === 'true'
  }

  // Комбинируем фильтрацию и сортировку
  const dataToRender = React.useMemo(() => {
    let data = filteredData ?? servers

    // Фильтр по МО
    if (showMo) {
      data = data?.filter(s => isFieldTrue(s.mo))
    }

    // Сортировка первичного массива по алфавиту (если нет активной сортировки)
    if (!sortState.order || !sortState.key) {
      if (data && data.length > 0) {
        data = [...data].sort((a, b) => {
          const aName = String(a?.name || '').toLowerCase().trim()
          const bName = String(b?.name || '').toLowerCase().trim()
          return aName.localeCompare(bName, 'ru', { numeric: true })
        })
      }
    }

    if (sortState.order && sortState.key) {
      // Если есть активная сортировка, применяем её к текущим данным
      const sorted = data && [...data]?.sort((a, b) => {
        let aValue = a?.[sortState.key]
        let bValue = b?.[sortState.key]

        // Обработка булевых значений (для полей mo, status и т.д.)
        if (typeof aValue === 'boolean' || aValue === 1 || aValue === 0 || aValue === '1' || aValue === '0' || aValue === 'true' || aValue === 'false') {
          const aBool = aValue === true || aValue === 1 || aValue === '1' || aValue === 'true'
          const bBool = bValue === true || bValue === 1 || bValue === '1' || bValue === 'true'
          if (sortState.order === 'asc') {
            return aBool === bBool ? 0 : aBool ? 1 : -1
          } else {
            return aBool === bBool ? 0 : aBool ? -1 : 1
          }
        }

        if (aValue === null || aValue === undefined) return 1
        if (bValue === null || bValue === undefined) return -1

        if (typeof aValue === 'number' && typeof bValue === 'number') {
          return sortState.order === 'asc' ? aValue - bValue : bValue - aValue
        }

        const aStr = String(aValue).toLowerCase()
        const bStr = String(bValue).toLowerCase()

        if (sortState.order === 'asc') {
          return aStr.localeCompare(bStr, 'ru', { numeric: true })
        } else {
          return bStr.localeCompare(aStr, 'ru', { numeric: true })
        }
      })
      return sorted
    }

    return data
  }, [filteredData, servers, sortState, showMo])

  const handleOpenModal = (server = null) => {
    setEditingServer(server)
    setIsModalOpen(true)
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
    setEditingServer(null)
  }

  const handleSubmit = async (serverData, serverId) => {
    let result
    if (serverId) {
      result = await dispatch(updateServer(serverId, serverData))
    } else {
      result = await dispatch(createServer(serverData))
    }

    if (result?.success) {
      handleCloseModal()
      dispatch(getServers())
    } else {
      alert(result?.error?.message || 'Произошла ошибка')
    }
  }

  const handleDelete = async (id) => {
    if (window.confirm('Вы уверены, что хотите удалить этот сервер?')) {
      const result = await dispatch(deleteServer(id))
      if (result?.success) {
        dispatch(getServers())
      } else {
        alert(result?.error?.message || 'Произошла ошибка при удалении')
      }
    }
  }

  const formattedDate = (date) => date ? moment(date).format('DD.MM.YYYY HH:mm:ss') : ''

  if (loading && !servers.length) return <Loader />

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <h2>Сервера ОО</h2>
        <button
          className={styles.addButton}
          onClick={() => handleOpenModal()}
        >
          <IoMdAdd size={20} />
          Добавить сервер
        </button>
      </div>

      <div className={styles.checkboxFilters}>
        <label className={styles.checkboxLabel}>
          <input
            type="checkbox"
            checked={showMo}
            onChange={(e) => setShowMo(e.target.checked)}
            className={styles.checkbox}
          />
          <span>МО</span>
        </label>
      </div>

      <section className={styles.tableSection}>
        <Table maxHeight='78vh' minHeight='500px' dataLoaded={!loading && servers?.length > 0}>
          <thead>
            <tr>
              <th>
                <div className={styles.headerCellWrapper}>
                  <div>Наименование</div>
                  <div>
                    <TableSearch
                      data={servers}
                      searchKey="name"
                      onFilter={handleFilter}
                      onSort={handleSort}
                      activeSortKey={sortState.key}
                      activeSortOrder={sortState.order}
                      onSearchOpen={handleSearchOpen}
                      openSearchKey={openSearchKey}
                    />
                  </div>
                </div>
              </th>
              <th>
                <div className={styles.headerCellWrapper}>
                  <div>Глобал, IP</div>
                  <div>
                    <TableSearch
                      data={servers}
                      searchKey="hostglobal"
                      onFilter={handleFilter}
                      onSort={handleSort}
                      activeSortKey={sortState.key}
                      activeSortOrder={sortState.order}
                      onSearchOpen={handleSearchOpen}
                      openSearchKey={openSearchKey}
                    />
                  </div>
                </div>
              </th>
              <th>
                <div className={styles.headerCellWrapper}>
                  <div>ID</div>
                  <div>
                    <TableSearch
                      data={servers}
                      searchKey="id"
                      onFilter={handleFilter}
                      onSort={handleSort}
                      activeSortKey={sortState.key}
                      activeSortOrder={sortState.order}
                      onSearchOpen={handleSearchOpen}
                      openSearchKey={openSearchKey}
                    />
                  </div>
                </div>
              </th>
              <th>
                <div className={styles.headerCellWrapper}>
                  <div>Порт</div>
                  <div>
                    <TableSearch
                      data={servers}
                      searchKey="port"
                      onFilter={handleFilter}
                      onSort={handleSort}
                      activeSortKey={sortState.key}
                      activeSortOrder={sortState.order}
                      onSearchOpen={handleSearchOpen}
                      openSearchKey={openSearchKey}
                    />
                  </div>
                </div>
              </th>
              <th>
                <div className={styles.headerCellWrapper}>
                  <div>База данных</div>
                  <div>
                    <TableSearch
                      data={servers}
                      searchKey="db"
                      onFilter={handleFilter}
                      onSort={handleSort}
                      activeSortKey={sortState.key}
                      activeSortOrder={sortState.order}
                      onSearchOpen={handleSearchOpen}
                      openSearchKey={openSearchKey}
                    />
                  </div>
                </div>
              </th>
              <th>
                <div className={styles.headerCellWrapper}>
                  <div>LMS</div>
                  <div>
                    <TableSearch
                      data={servers}
                      searchKey="status"
                      onFilter={handleFilter}
                      onSort={handleSort}
                      activeSortKey={sortState.key}
                      activeSortOrder={sortState.order}
                      onSearchOpen={handleSearchOpen}
                      openSearchKey={openSearchKey}
                    />
                  </div>
                </div>
              </th>
              <th>
                <div className={styles.headerCellWrapper}>
                  <div>Версия</div>
                  <div>
                    <TableSearch
                      data={servers}
                      searchKey="version"
                      onFilter={handleFilter}
                      onSort={handleSort}
                      activeSortKey={sortState.key}
                      activeSortOrder={sortState.order}
                      onSearchOpen={handleSearchOpen}
                      openSearchKey={openSearchKey}
                    />
                  </div>
                </div>
              </th>
              <th>
                <div className={styles.headerCellWrapper}>
                  <div>Успешное подкл.</div>
                  <div>
                    <TableSearch
                      data={servers}
                      searchKey="time_good"
                      onFilter={handleFilter}
                      onSort={handleSort}
                      activeSortKey={sortState.key}
                      activeSortOrder={sortState.order}
                      onSearchOpen={handleSearchOpen}
                      openSearchKey={openSearchKey}
                    />
                  </div>
                </div>
              </th>
              <th>
                <div className={styles.headerCellWrapper}>
                  <div>Неудачное подкл.</div>
                  <div>
                    <TableSearch
                      data={servers}
                      searchKey="time_bad"
                      onFilter={handleFilter}
                      onSort={handleSort}
                      activeSortKey={sortState.key}
                      activeSortOrder={sortState.order}
                      onSearchOpen={handleSearchOpen}
                      openSearchKey={openSearchKey}
                    />
                  </div>
                </div>
              </th>
              <th>
                <div className={styles.headerCellWrapper}>
                  <div>Бэкап дата</div>
                  <div>
                    <TableSearch
                      data={servers}
                      searchKey="lastbackup_date"
                      onFilter={handleFilter}
                      onSort={handleSort}
                      activeSortKey={sortState.key}
                      activeSortOrder={sortState.order}
                      onSearchOpen={handleSearchOpen}
                      openSearchKey={openSearchKey}
                    />
                  </div>
                </div>
              </th>
              <th>
                <div className={styles.headerCellWrapper}>
                  <div>Бэкап размер</div>
                  <div>
                    <TableSearch
                      data={servers}
                      searchKey="lastbackup_size"
                      onFilter={handleFilter}
                      onSort={handleSort}
                      activeSortKey={sortState.key}
                      activeSortOrder={sortState.order}
                      onSearchOpen={handleSearchOpen}
                      openSearchKey={openSearchKey}
                    />
                  </div>
                </div>
              </th>
              <th>
                <div className={styles.headerCellWrapper}>
                  <div>Последний вход</div>
                  <div>
                    <TableSearch
                      data={servers}
                      searchKey="lastlogin"
                      onFilter={handleFilter}
                      onSort={handleSort}
                      activeSortKey={sortState.key}
                      activeSortOrder={sortState.order}
                      onSearchOpen={handleSearchOpen}
                      openSearchKey={openSearchKey}
                    />
                  </div>
                </div>
              </th>
              <th>Действия</th>
            </tr>
          </thead>
          <tbody>
            {dataToRender?.length > 0 ? (
              dataToRender.map((item) => (
                <tr key={item.id}>
                  <td>{item.name}</td>
                  <td>{item.hostglobal}</td>
                  <td>{item.id}</td>
                  <td>{item.port}</td>
                  <td>{item.db}</td>
                  <td>
                    {item.status ? (
                      <IoIosCheckmark color='#00d900' size={24} />
                    ) : (
                      <IoIosClose color='red' size={24} />
                    )}
                  </td>
                  <td>{item.version || '-'}</td>
                  <td>{formattedDate(item.time_good)}</td>
                  <td>{formattedDate(item.time_bad)}</td>
                  <td>{formattedDate(item.lastbackup_date)}</td>
                  <td>{item.lastbackup_size || '-'}</td>
                  <td>{formattedDate(item.lastlogin)}</td>
                  <td>
                    <div className={styles.actions}>
                      <button
                        className={styles.editButton}
                        onClick={() => handleOpenModal(item)}
                        title="Редактировать"
                      >
                        <IoMdCreate size={18} />
                      </button>
                      <button
                        className={styles.deleteButton}
                        onClick={() => handleDelete(item.id)}
                        title="Удалить"
                      >
                        <IoMdTrash size={18} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="13" className={styles.emptyMessage}>
                  Нет данных
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </section>

      <ServerModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        editingServer={editingServer}
        onSubmit={handleSubmit}
        loading={loading}
      />
    </div>
  )
}

export default Servers

